import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiFolder, 
  FiFolderPlus, 
  FiChevronRight, 
  FiChevronDown, 
  FiFileText, 
  FiLayers, 
  FiSearch,
  FiCornerDownRight,
  FiTrendingUp,
  FiTrendingDown,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiBriefcase
} from 'react-icons/fi';
import { toast } from 'sonner';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import { EssentialPlanRequiredCard } from '../components/EssentialPlanRequiredCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_PALETTE = [
  '#10B981', // Emerald
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#14B8A6', // Teal
  '#64748B', // Slate
];

export const CarteirasContas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  const isTrial = Boolean(
    !isAdmin &&
    user?.plan_type !== 'basic' &&
    user?.plan_type !== 'essential' &&
    (user?.trial_period || user?.plan_type === 'trial' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date()))
  );
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || isTrial;
  const isBasic = !isAdmin && isSubscriptionActive && user?.plan_type === 'basic';

  const [treeData, setTreeData] = useState({ entradas: [], saidas: [], total_groups: 0, total_accounts: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all'); // 'all', 'entrada', 'saida'

  // Tree Expansion state:
  // Root branches (1 - Entradas, 2 - Saídas)
  const [rootExpanded, setRootExpanded] = useState({
    entradas: true,
    saidas: true,
  });

  // Group nodes expansion state
  const [expandedGroups, setExpandedGroups] = useState({});

  // ============= MODALS STATE =============
  // 1. Group Modal
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupTipo, setGroupTipo] = useState('entrada'); // 'entrada' | 'saida'
  const [groupName, setGroupName] = useState('');
  const [groupColor, setGroupColor] = useState('#10B981');
  const [savingGroup, setSavingGroup] = useState(false);

  // 2. Account Modal
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);

  // 3. Delete Confirmation Modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'group' | 'account', data: {...} }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isSubscriptionActive) {
      loadAccountsTree();
    } else {
      setLoading(false);
    }
  }, [isSubscriptionActive]);

  const loadAccountsTree = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/carteiras/contas/tree`, { withCredentials: true });
      const data = res.data || { entradas: [], saidas: [], total_groups: 0, total_accounts: 0 };
      setTreeData(data);

      // Auto-expand all groups by default
      const exp = {};
      (data.entradas || []).forEach(g => { exp[g.group_id] = true; });
      (data.saidas || []).forEach(g => { exp[g.group_id] = true; });
      setExpandedGroups(exp);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar o plano de contas');
    } finally {
      setLoading(false);
    }
  };

  const toggleRoot = (rootKey) => {
    setRootExpanded(prev => ({
      ...prev,
      [rootKey]: !prev[rootKey]
    }));
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const expandAll = () => {
    setRootExpanded({ entradas: true, saidas: true });
    const exp = {};
    (treeData.entradas || []).forEach(g => { exp[g.group_id] = true; });
    (treeData.saidas || []).forEach(g => { exp[g.group_id] = true; });
    setExpandedGroups(exp);
  };

  const collapseAll = () => {
    setRootExpanded({ entradas: false, saidas: false });
    setExpandedGroups({});
  };

  // All groups combined for dropdowns
  const allGroups = useMemo(() => {
    return [
      ...(treeData.entradas || []).map(g => ({ ...g, tipo: 'entrada' })),
      ...(treeData.saidas || []).map(g => ({ ...g, tipo: 'saida' })),
    ];
  }, [treeData]);

  // Search and filter logic
  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filterList = (groups) => {
      if (!q) return groups;
      return groups.map(g => {
        const matchesGroup = g.name.toLowerCase().includes(q);
        const matchedAccounts = (g.accounts || []).filter(a => a.name.toLowerCase().includes(q));
        if (matchesGroup || matchedAccounts.length > 0) {
          return {
            ...g,
            accounts: matchesGroup ? g.accounts : matchedAccounts
          };
        }
        return null;
      }).filter(Boolean);
    };

    return {
      entradas: (tipoFilter === 'all' || tipoFilter === 'entrada') ? filterList(treeData.entradas || []) : [],
      saidas: (tipoFilter === 'all' || tipoFilter === 'saida') ? filterList(treeData.saidas || []) : [],
    };
  }, [treeData, search, tipoFilter]);

  // Count calculations
  const totalEntradasCount = useMemo(() => {
    return (treeData.entradas || []).reduce((acc, g) => acc + (g.accounts?.length || 0), 0);
  }, [treeData]);

  const totalSaidasCount = useMemo(() => {
    return (treeData.saidas || []).reduce((acc, g) => acc + (g.accounts?.length || 0), 0);
  }, [treeData]);

  // ============= GROUP HANDLERS =============
  const handleOpenCreateGroup = (preferredTipo = 'entrada') => {
    setEditingGroup(null);
    setGroupTipo(preferredTipo);
    setGroupName('');
    setGroupColor(preferredTipo === 'entrada' ? '#10B981' : '#EF4444');
    setGroupModalOpen(true);
  };

  const handleOpenEditGroup = (group) => {
    setEditingGroup(group);
    setGroupTipo(group.tipo || 'entrada');
    setGroupName(group.name || '');
    setGroupColor(group.color || (group.tipo === 'entrada' ? '#10B981' : '#EF4444'));
    setGroupModalOpen(true);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    const nameTrimmed = groupName.trim();
    if (!nameTrimmed) {
      toast.error('Informe o nome do grupo');
      return;
    }

    setSavingGroup(true);
    try {
      const payload = {
        name: nameTrimmed,
        tipo: groupTipo,
        color: groupColor
      };

      if (editingGroup) {
        await axios.put(`${API}/carteiras/contas/groups/${editingGroup.group_id}`, payload, { withCredentials: true });
        toast.success('Grupo atualizado com sucesso!');
      } else {
        await axios.post(`${API}/carteiras/contas/groups`, payload, { withCredentials: true });
        toast.success(`Grupo cadastrado em ${groupTipo === 'entrada' ? '1 - Entradas' : '2 - Saídas'}!`);
      }
      setGroupModalOpen(false);
      loadAccountsTree();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao salvar grupo');
    } finally {
      setSavingGroup(false);
    }
  };

  // ============= ACCOUNT HANDLERS =============
  const handleOpenCreateAccount = (preselectedGroupId = '') => {
    setEditingAccount(null);
    setAccountName('');
    const initialGroupId = preselectedGroupId || (allGroups[0]?.group_id || '');
    setSelectedGroupId(initialGroupId);
    setAccountModalOpen(true);
  };

  const handleOpenEditAccount = (account) => {
    setEditingAccount(account);
    setAccountName(account.name || '');
    setSelectedGroupId(account.group_id || '');
    setAccountModalOpen(true);
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    const nameTrimmed = accountName.trim();
    if (!nameTrimmed) {
      toast.error('Informe o nome da conta');
      return;
    }

    if (!selectedGroupId) {
      toast.error('Selecione o Grupo Pai da conta');
      return;
    }

    setSavingAccount(true);
    try {
      if (editingAccount) {
        await axios.put(
          `${API}/carteiras/contas/accounts/${editingAccount.account_id}`,
          { name: nameTrimmed, group_id: selectedGroupId },
          { withCredentials: true }
        );
        toast.success('Conta atualizada com sucesso!');
      } else {
        await axios.post(
          `${API}/carteiras/contas/accounts`,
          { name: nameTrimmed, group_id: selectedGroupId },
          { withCredentials: true }
        );
        toast.success('Conta adicionada ao grupo com sucesso!');
      }
      setAccountModalOpen(false);
      loadAccountsTree();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao salvar conta');
    } finally {
      setSavingAccount(false);
    }
  };

  // ============= DELETE CONFIRMATION HANDLERS =============
  const handlePromptDeleteGroup = (group) => {
    setItemToDelete({ type: 'group', data: group });
    setDeleteConfirmOpen(true);
  };

  const handlePromptDeleteAccount = (account) => {
    setItemToDelete({ type: 'account', data: account });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);

    try {
      if (itemToDelete.type === 'group') {
        await axios.delete(`${API}/carteiras/contas/groups/${itemToDelete.data.group_id}`, { withCredentials: true });
        toast.success('Grupo e suas contas excluídos');
      } else {
        await axios.delete(`${API}/carteiras/contas/accounts/${itemToDelete.data.account_id}`, { withCredentials: true });
        toast.success('Conta excluída com sucesso');
      }
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadAccountsTree();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Plano de Contas" />
        </main>
      </div>
    );
  }

  if (isBasic) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <EssentialPlanRequiredCard module="carteiras" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-slate-950">
      <Header />

      <main className="transition-all duration-300">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* ================= PAGE TITLE & MAIN ACTIONS ================= */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5 rounded-full blur-3xl -z-10 pointer-events-none" />

          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/60 border border-blue-200/60 dark:border-blue-800 text-blue-700 dark:text-blue-400 text-xs font-semibold tracking-wide">
              <FiLayers size={13} />
              Módulo Carteiras
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-50 tracking-tight flex items-center gap-3">
              Plano de Contas
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
              Estrutura hierárquica em árvore de Grupos e Contas exclusiva para a gestão financeira do módulo de Carteiras.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={() => handleOpenCreateGroup('entrada')}
              className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl gap-2 font-semibold h-11 px-4 shadow-sm transition-all"
            >
              <FiFolderPlus size={18} className="text-emerald-600 dark:text-emerald-400" />
              + Novo Grupo
            </Button>

            <Button
              onClick={() => handleOpenCreateAccount()}
              className="bg-blue-900 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-semibold rounded-2xl gap-2 shadow-md hover:shadow-lg h-11 px-5 transition-all"
            >
              <FiPlus size={18} />
              + Nova Conta
            </Button>
          </div>
        </div>

        {/* ================= SUMMARY STATS CARDS ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
              <FiTrendingUp size={24} />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">1 - Entradas</span>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
                {treeData.entradas?.length || 0} <span className="text-xs font-normal text-slate-500">grupos</span>
              </div>
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                {totalEntradasCount} contas ativas
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
              <FiTrendingDown size={24} />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">2 - Saídas</span>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
                {treeData.saidas?.length || 0} <span className="text-xs font-normal text-slate-500">grupos</span>
              </div>
              <span className="text-xs text-rose-600 dark:text-rose-400 font-medium">
                {totalSaidasCount} contas ativas
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
              <FiFolder size={24} />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total de Grupos</span>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
                {treeData.total_groups || 0}
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Nível 1 da árvore
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
              <FiFileText size={24} />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total de Contas</span>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
                {treeData.total_accounts || 0}
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Nível 2 da árvore
              </span>
            </div>
          </div>
        </div>

        {/* ================= TOOLBAR: SEARCH & ACTIONS ================= */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar grupo ou conta..."
                className="pl-10 h-10 bg-slate-50/80 dark:bg-slate-800/60 rounded-xl border-slate-200 dark:border-slate-700 text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Filter by Tipo Pill Tabs */}
            <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200/50 dark:border-slate-700 text-xs font-semibold">
              <button
                onClick={() => setTipoFilter('all')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  tipoFilter === 'all'
                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setTipoFilter('entrada')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                  tipoFilter === 'entrada'
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
              >
                <FiTrendingUp size={12} />
                Entradas
              </button>
              <button
                onClick={() => setTipoFilter('saida')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                  tipoFilter === 'saida'
                    ? 'bg-rose-500 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400'
                }`}
              >
                <FiTrendingDown size={12} />
                Saídas
              </button>
            </div>
          </div>

          {/* Expand / Collapse Controls */}
          <div className="flex items-center gap-2 self-end sm:self-auto text-xs font-semibold text-slate-600 dark:text-slate-300">
            <button
              onClick={expandAll}
              className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
            >
              Expandir Tudo
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
            >
              Recolher Tudo
            </button>
          </div>
        </div>

        {/* ================= MAIN TREE VIEW ================= */}
        <div className="space-y-6">
          {loading ? (
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-16 text-center border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="inline-block animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Carregando plano de contas...</p>
            </div>
          ) : (
            <>
              {/* ================= 1 - ENTRADAS (RAIZ FIXA) ================= */}
              {(tipoFilter === 'all' || tipoFilter === 'entrada') && (
                <div className="bg-white dark:bg-slate-900 rounded-3xl border border-emerald-500/20 dark:border-emerald-500/30 shadow-sm overflow-hidden transition-all">
                  
                  {/* Root Node Header */}
                  <div className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent dark:from-emerald-500/20 dark:via-emerald-950/30 dark:to-slate-900 p-4 sm:p-5 flex items-center justify-between border-b border-emerald-500/15 dark:border-emerald-500/20">
                    <button
                      onClick={() => toggleRoot('entradas')}
                      className="flex items-center gap-3 text-left group flex-1 focus:outline-none"
                    >
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/20 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center transition-transform group-hover:scale-105">
                        {rootExpanded.entradas ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                      </div>
                      
                      <div className="flex items-center gap-2.5">
                        <span className="text-xl">📁</span>
                        <div>
                          <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            1 - Entradas
                            <Badge className="bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800 text-xs font-semibold px-2 py-0.5">
                              Receitas
                            </Badge>
                          </h2>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {filteredData.entradas.length} grupos • {totalEntradasCount} contas
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Quick Add Group on Entrada */}
                    <Button
                      size="sm"
                      onClick={() => handleOpenCreateGroup('entrada')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-1.5 text-xs font-semibold h-9 px-3.5 shadow-xs"
                    >
                      <FiPlus size={14} />
                      + Grupo em Entradas
                    </Button>
                  </div>

                  {/* Root Children (Groups of Entrada) */}
                  {rootExpanded.entradas && (
                    <div className="p-4 sm:p-6 space-y-3">
                      {filteredData.entradas.length === 0 ? (
                        <div className="py-10 text-center text-slate-500 dark:text-slate-400 text-sm space-y-3 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                          <p>Nenhum grupo de entrada encontrado.</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenCreateGroup('entrada')}
                            className="rounded-xl border-emerald-600/30 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs"
                          >
                            <FiPlus size={14} /> Cadastrar Grupo de Entrada
                          </Button>
                        </div>
                      ) : (
                        filteredData.entradas.map((group) => {
                          const isExp = expandedGroups[group.group_id] ?? true;
                          const accounts = group.accounts || [];

                          return (
                            <div
                              key={group.group_id}
                              className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/60 overflow-hidden transition-all hover:border-emerald-500/40 dark:hover:border-emerald-500/40"
                            >
                              {/* Group Row (Nível 1) */}
                              <div className="p-3 sm:p-4 flex items-center justify-between gap-3 bg-white dark:bg-slate-900/90">
                                <button
                                  onClick={() => toggleGroup(group.group_id)}
                                  className="flex items-center gap-3 text-left flex-1 focus:outline-none group"
                                >
                                  <div className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-colors">
                                    {isExp ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                                  </div>

                                  <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-xs flex-shrink-0"
                                    style={{ backgroundColor: group.color || '#10B981' }}
                                  >
                                    <FiFolder size={16} />
                                  </div>

                                  <div>
                                    <div className="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                      {group.name}
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                        {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}
                                      </span>
                                    </div>
                                  </div>
                                </button>

                                {/* Group Actions */}
                                <div className="flex items-center gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenCreateAccount(group.group_id)}
                                    title="Adicionar conta a este grupo"
                                    className="h-8 px-2.5 rounded-xl border-emerald-600/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-semibold gap-1"
                                  >
                                    <FiPlus size={13} />
                                    <span className="hidden sm:inline">Nova Conta</span>
                                  </Button>

                                  <button
                                    onClick={() => handleOpenEditGroup(group)}
                                    title="Editar Grupo"
                                    className="p-2 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>

                                  <button
                                    onClick={() => handlePromptDeleteGroup(group)}
                                    title="Excluir Grupo"
                                    className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition-colors"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </div>

                              {/* Nested Accounts (Nível 2) */}
                              {isExp && (
                                <div className="px-4 sm:px-6 pb-3 pt-1 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/70 dark:bg-slate-950/40">
                                  {accounts.length === 0 ? (
                                    <div className="py-4 pl-8 text-xs text-slate-400 dark:text-slate-500 italic flex items-center justify-between">
                                      <span>Nenhuma conta vinculada a este grupo.</span>
                                      <button
                                        onClick={() => handleOpenCreateAccount(group.group_id)}
                                        className="text-emerald-600 dark:text-emerald-400 hover:underline font-semibold not-italic"
                                      >
                                        + Adicionar Primeira Conta
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                      {accounts.map((acc) => (
                                        <div
                                          key={acc.account_id}
                                          className="py-2.5 pl-6 sm:pl-8 flex items-center justify-between gap-3 group/acc hover:bg-white/60 dark:hover:bg-slate-800/40 rounded-xl px-2 transition-colors relative"
                                        >
                                          <div className="flex items-center gap-2.5">
                                            <FiCornerDownRight className="text-slate-300 dark:text-slate-600 flex-shrink-0" size={14} />
                                            <div className="w-6 h-6 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                                              <FiFileText size={12} />
                                            </div>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                              {acc.name}
                                            </span>
                                          </div>

                                          <div className="flex items-center gap-1 opacity-80 sm:opacity-0 sm:group-hover/acc:opacity-100 transition-opacity">
                                            <button
                                              onClick={() => handleOpenEditAccount(acc)}
                                              title="Editar Conta"
                                              className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                            >
                                              <FiEdit2 size={13} />
                                            </button>
                                            <button
                                              onClick={() => handlePromptDeleteAccount(acc)}
                                              title="Excluir Conta"
                                              className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                                            >
                                              <FiTrash2 size={13} />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ================= 2 - SAÍDAS (RAIZ FIXA) ================= */}
              {(tipoFilter === 'all' || tipoFilter === 'saida') && (
                <div className="bg-white dark:bg-slate-900 rounded-3xl border border-rose-500/20 dark:border-rose-500/30 shadow-sm overflow-hidden transition-all">
                  
                  {/* Root Node Header */}
                  <div className="bg-gradient-to-r from-rose-500/10 via-rose-500/5 to-transparent dark:from-rose-500/20 dark:via-rose-950/30 dark:to-slate-900 p-4 sm:p-5 flex items-center justify-between border-b border-rose-500/15 dark:border-rose-500/20">
                    <button
                      onClick={() => toggleRoot('saidas')}
                      className="flex items-center gap-3 text-left group flex-1 focus:outline-none"
                    >
                      <div className="w-8 h-8 rounded-xl bg-rose-500/20 dark:bg-rose-500/30 text-rose-700 dark:text-rose-300 flex items-center justify-center transition-transform group-hover:scale-105">
                        {rootExpanded.saidas ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                      </div>
                      
                      <div className="flex items-center gap-2.5">
                        <span className="text-xl">📁</span>
                        <div>
                          <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            2 - Saídas
                            <Badge className="bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-800 text-xs font-semibold px-2 py-0.5">
                              Despesas
                            </Badge>
                          </h2>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {filteredData.saidas.length} grupos • {totalSaidasCount} contas
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Quick Add Group on Saida */}
                    <Button
                      size="sm"
                      onClick={() => handleOpenCreateGroup('saida')}
                      className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl gap-1.5 text-xs font-semibold h-9 px-3.5 shadow-xs"
                    >
                      <FiPlus size={14} />
                      + Grupo em Saídas
                    </Button>
                  </div>

                  {/* Root Children (Groups of Saida) */}
                  {rootExpanded.saidas && (
                    <div className="p-4 sm:p-6 space-y-3">
                      {filteredData.saidas.length === 0 ? (
                        <div className="py-10 text-center text-slate-500 dark:text-slate-400 text-sm space-y-3 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                          <p>Nenhum grupo de saída encontrado.</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenCreateGroup('saida')}
                            className="rounded-xl border-rose-600/30 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 text-xs"
                          >
                            <FiPlus size={14} /> Cadastrar Grupo de Saída
                          </Button>
                        </div>
                      ) : (
                        filteredData.saidas.map((group) => {
                          const isExp = expandedGroups[group.group_id] ?? true;
                          const accounts = group.accounts || [];

                          return (
                            <div
                              key={group.group_id}
                              className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/60 overflow-hidden transition-all hover:border-rose-500/40 dark:hover:border-rose-500/40"
                            >
                              {/* Group Row (Nível 1) */}
                              <div className="p-3 sm:p-4 flex items-center justify-between gap-3 bg-white dark:bg-slate-900/90">
                                <button
                                  onClick={() => toggleGroup(group.group_id)}
                                  className="flex items-center gap-3 text-left flex-1 focus:outline-none group"
                                >
                                  <div className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-colors">
                                    {isExp ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                                  </div>

                                  <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-xs flex-shrink-0"
                                    style={{ backgroundColor: group.color || '#EF4444' }}
                                  >
                                    <FiFolder size={16} />
                                  </div>

                                  <div>
                                    <div className="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                      {group.name}
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                        {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}
                                      </span>
                                    </div>
                                  </div>
                                </button>

                                {/* Group Actions */}
                                <div className="flex items-center gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenCreateAccount(group.group_id)}
                                    title="Adicionar conta a este grupo"
                                    className="h-8 px-2.5 rounded-xl border-rose-600/30 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950 text-xs font-semibold gap-1"
                                  >
                                    <FiPlus size={13} />
                                    <span className="hidden sm:inline">Nova Conta</span>
                                  </Button>

                                  <button
                                    onClick={() => handleOpenEditGroup(group)}
                                    title="Editar Grupo"
                                    className="p-2 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>

                                  <button
                                    onClick={() => handlePromptDeleteGroup(group)}
                                    title="Excluir Grupo"
                                    className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition-colors"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </div>

                              {/* Nested Accounts (Nível 2) */}
                              {isExp && (
                                <div className="px-4 sm:px-6 pb-3 pt-1 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/70 dark:bg-slate-950/40">
                                  {accounts.length === 0 ? (
                                    <div className="py-4 pl-8 text-xs text-slate-400 dark:text-slate-500 italic flex items-center justify-between">
                                      <span>Nenhuma conta vinculada a este grupo.</span>
                                      <button
                                        onClick={() => handleOpenCreateAccount(group.group_id)}
                                        className="text-rose-600 dark:text-rose-400 hover:underline font-semibold not-italic"
                                      >
                                        + Adicionar Primeira Conta
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                      {accounts.map((acc) => (
                                        <div
                                          key={acc.account_id}
                                          className="py-2.5 pl-6 sm:pl-8 flex items-center justify-between gap-3 group/acc hover:bg-white/60 dark:hover:bg-slate-800/40 rounded-xl px-2 transition-colors relative"
                                        >
                                          <div className="flex items-center gap-2.5">
                                            <FiCornerDownRight className="text-slate-300 dark:text-slate-600 flex-shrink-0" size={14} />
                                            <div className="w-6 h-6 rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                                              <FiFileText size={12} />
                                            </div>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                              {acc.name}
                                            </span>
                                          </div>

                                          <div className="flex items-center gap-1 opacity-80 sm:opacity-0 sm:group-hover/acc:opacity-100 transition-opacity">
                                            <button
                                              onClick={() => handleOpenEditAccount(acc)}
                                              title="Editar Conta"
                                              className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                            >
                                              <FiEdit2 size={13} />
                                            </button>
                                            <button
                                              onClick={() => handlePromptDeleteAccount(acc)}
                                              title="Excluir Conta"
                                              className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                                            >
                                              <FiTrash2 size={13} />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </div>

      </main>

      {/* ========================================================================= */}
      {/* 1. MODAL CADASTRO / EDIÇÃO DE GRUPO (NÍVEL 1)                             */}
      {/* ========================================================================= */}
      <Dialog open={groupModalOpen} onOpenChange={setGroupModalOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <FiFolderPlus size={20} />
              </div>
              {editingGroup ? 'Editar Grupo' : 'Novo Grupo (Nível 1)'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
              Grupos organizam suas contas sob as raízes de Entrada (Receitas) ou Saída (Despesas).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveGroup} className="space-y-5 mt-2">
            {/* 1. Tipo (Obrigatório) */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Tipo do Grupo (Obrigatório) <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setGroupTipo('entrada');
                    if (!editingGroup) setGroupColor('#10B981');
                  }}
                  className={`p-3.5 rounded-2xl border-2 text-left flex items-center gap-3 transition-all ${
                    groupTipo === 'entrada'
                      ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 shadow-xs'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    groupTipo === 'entrada' ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 dark:border-slate-600'
                  }`}>
                    {groupTipo === 'entrada' && <FiCheck size={12} strokeWidth={3} />}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <FiTrendingUp className="text-emerald-500" size={15} />
                      1 - Entrada
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Receitas & Entradas</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setGroupTipo('saida');
                    if (!editingGroup) setGroupColor('#EF4444');
                  }}
                  className={`p-3.5 rounded-2xl border-2 text-left flex items-center gap-3 transition-all ${
                    groupTipo === 'saida'
                      ? 'border-rose-500 bg-rose-50/60 dark:bg-rose-950/40 text-rose-900 dark:text-rose-200 shadow-xs'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    groupTipo === 'saida' ? 'border-rose-600 bg-rose-600 text-white' : 'border-slate-300 dark:border-slate-600'
                  }`}>
                    {groupTipo === 'saida' && <FiCheck size={12} strokeWidth={3} />}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <FiTrendingDown className="text-rose-500" size={15} />
                      2 - Saída
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Despesas & Custos</div>
                  </div>
                </button>
              </div>
            </div>

            {/* 2. Nome do Grupo */}
            <div className="space-y-2">
              <Label htmlFor="group-name" className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Nome do Grupo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="group-name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ex: Despesas Bancárias, Vendas de Serviços, Impostos"
                required
                className="h-11 rounded-xl bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-sm"
              />
            </div>

            {/* 3. Cor Visual de Destaque */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Cor de Destaque do Grupo
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setGroupColor(c)}
                    style={{ backgroundColor: c }}
                    className={`w-7 h-7 rounded-xl flex items-center justify-center text-white transition-all ${
                      groupColor === c ? 'ring-2 ring-offset-2 ring-blue-600 scale-110' : 'opacity-80 hover:opacity-100'
                    }`}
                  >
                    {groupColor === c && <FiCheck size={14} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="outline"
                onClick={() => setGroupModalOpen(false)}
                disabled={savingGroup}
                className="rounded-xl h-10 px-4 text-xs font-semibold"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingGroup}
                className="bg-blue-900 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-xl h-10 px-5 text-xs font-semibold"
              >
                {savingGroup ? 'Salvando...' : editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* 2. MODAL CADASTRO / EDIÇÃO DE CONTA (NÍVEL 2)                             */}
      {/* ========================================================================= */}
      <Dialog open={accountModalOpen} onOpenChange={setAccountModalOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <FiFileText size={20} />
              </div>
              {editingAccount ? 'Editar Conta' : 'Nova Conta / Subcategoria (Nível 2)'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
              A conta fica aninhada imediatamente abaixo do Grupo Pai selecionado.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveAccount} className="space-y-5 mt-2">
            {/* 1. Grupo Pai */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Grupo Pai <span className="text-red-500">*</span>
              </Label>
              {allGroups.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                  Nenhum grupo cadastrado. Por favor, crie um grupo primeiro antes de cadastrar uma conta.
                </div>
              ) : (
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-sm">
                    <SelectValue placeholder="Selecione o grupo pai..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    {/* Entradas */}
                    {treeData.entradas?.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider bg-emerald-50/50 dark:bg-emerald-950/30 rounded-md my-1">
                        ── 1 - ENTRADAS (RECEITAS) ──
                      </div>
                    )}
                    {(treeData.entradas || []).map((g) => (
                      <SelectItem key={g.group_id} value={g.group_id} className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.color || '#10B981' }} />
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{g.name}</span>
                          <span className="text-xs text-emerald-600 font-normal">(Entrada)</span>
                        </div>
                      </SelectItem>
                    ))}

                    {/* Saídas */}
                    {treeData.saidas?.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider bg-rose-50/50 dark:bg-rose-950/30 rounded-md my-1">
                        ── 2 - SAÍDAS (DESPESAS) ──
                      </div>
                    )}
                    {(treeData.saidas || []).map((g) => (
                      <SelectItem key={g.group_id} value={g.group_id} className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.color || '#EF4444' }} />
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{g.name}</span>
                          <span className="text-xs text-rose-600 font-normal">(Saída)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* 2. Nome da Conta */}
            <div className="space-y-2">
              <Label htmlFor="account-name" className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Nome da Conta <span className="text-red-500">*</span>
              </Label>
              <Input
                id="account-name"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="Ex: TED, Boleto, Pix, Comissão, Salários, Aluguel"
                required
                className="h-11 rounded-xl bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-sm"
              />
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAccountModalOpen(false)}
                disabled={savingAccount}
                className="rounded-xl h-10 px-4 text-xs font-semibold"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingAccount || allGroups.length === 0}
                className="bg-blue-900 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-xl h-10 px-5 text-xs font-semibold"
              >
                {savingAccount ? 'Salvando...' : editingAccount ? 'Salvar Alterações' : 'Criar Conta'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* 3. MODAL CONFIRMAÇÃO DE EXCLUSÃO                                          */}
      {/* ========================================================================= */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center">
                <FiAlertTriangle size={20} />
              </div>
              Confirmar Exclusão
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 pt-2">
              {itemToDelete?.type === 'group' ? (
                <>
                  Tem certeza que deseja excluir o grupo{' '}
                  <strong className="text-slate-800 dark:text-slate-200">
                    "{itemToDelete.data.name}"
                  </strong>
                  ?
                  <br />
                  <span className="text-red-600 dark:text-red-400 font-semibold block mt-1">
                    Atenção: Todas as contas vinculadas a este grupo também serão excluídas permanentemente!
                  </span>
                </>
              ) : (
                <>
                  Tem certeza que deseja excluir a conta{' '}
                  <strong className="text-slate-800 dark:text-slate-200">
                    "{itemToDelete?.data?.name}"
                  </strong>
                  ?
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 mt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deleting}
              className="rounded-xl h-10 px-4 text-xs font-semibold"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl h-10 px-5 text-xs font-semibold shadow-sm"
            >
              {deleting ? 'Excluindo...' : 'Sim, Excluir'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
